import { useEffect, useRef, useState } from "react";
import { getAnprEvents, fetchAnprSnapshotObjectUrl } from "../../utils/driveThruApi";
import { C } from "../reporting/reportingStyles";

// Draw the "stop box" — the region the on-prem detector watches to decide a car has
// stopped. Coordinates are stored as fractions of the frame (0..1) so they stay valid
// at any camera resolution. v1 = a rectangle (2 points); polygons render as their
// bounding box and are re-drawable as a rect. Background = the camera's latest ANPR
// snapshot when available (admin can't reach the camera directly), else an uploaded
// reference photo.

const DEFAULT_MIN_OCCUPANCY = 0.5;
const round = (n) => Math.round(n * 1000) / 1000;

function orderRect(a, b) {
  return {
    x1: Math.min(a.x, b.x),
    y1: Math.min(a.y, b.y),
    x2: Math.max(a.x, b.x),
    y2: Math.max(a.y, b.y),
  };
}

function rectFromValue(value) {
  if (!value?.points?.length) return null;
  if (value.type === "rect" && value.points.length >= 2) {
    const [p1, p2] = value.points;
    return orderRect({ x: p1[0], y: p1[1] }, { x: p2[0], y: p2[1] });
  }
  // polygon (or anything else): show its bounding box
  const xs = value.points.map((p) => p[0]);
  const ys = value.points.map((p) => p[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const smallBtn = {
  background: C.bg,
  border: `1px solid ${C.border}`,
  color: C.text,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 12,
  padding: "5px 10px",
  cursor: "pointer",
};

const StopBoxEditor = ({ value, onChange, capturePointId }) => {
  const wrapRef = useRef(null);
  const [bgUrl, setBgUrl] = useState(null);
  const [bgIsBlob, setBgIsBlob] = useState(false);
  const [snapState, setSnapState] = useState("idle"); // idle | loading | none
  const [drag, setDrag] = useState(null); // { start:{x,y}, cur:{x,y} }

  // Best-effort: load the camera's most recent ANPR snapshot to draw on (edit mode).
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      if (!capturePointId) {
        setSnapState("none");
        return;
      }
      setSnapState("loading");
      const res = await getAnprEvents({ capturePointId, pageSize: 1, pageNo: 1 });
      const ev = res?.events?.[0];
      if (ev?.id) {
        const url = await fetchAnprSnapshotObjectUrl(ev.id);
        if (url && !cancelled) {
          objectUrl = url;
          setBgUrl(url);
          setBgIsBlob(true);
          setSnapState("idle");
          return;
        }
      }
      if (!cancelled) setSnapState("none");
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [capturePointId]);

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (bgUrl && bgIsBlob) URL.revokeObjectURL(bgUrl);
    setBgUrl(URL.createObjectURL(file));
    setBgIsBlob(true);
    setSnapState("idle");
  };

  const toNorm = (clientX, clientY) => {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
    };
  };

  const onDown = (e) => {
    const p = toNorm(e.clientX, e.clientY);
    setDrag({ start: p, cur: p });
  };
  const onMove = (e) => {
    if (!drag) return;
    setDrag((d) => ({ ...d, cur: toNorm(e.clientX, e.clientY) }));
  };
  const finishDrag = () => {
    if (!drag) return;
    const r = orderRect(drag.start, drag.cur);
    setDrag(null);
    // Ignore accidental clicks / slivers.
    if (r.x2 - r.x1 < 0.02 || r.y2 - r.y1 < 0.02) return;
    onChange({
      type: "rect",
      points: [
        [round(r.x1), round(r.y1)],
        [round(r.x2), round(r.y2)],
      ],
      min_occupancy: value?.min_occupancy ?? DEFAULT_MIN_OCCUPANCY,
    });
  };

  const setMinOccupancy = (v) => {
    const rect = rectFromValue(value);
    if (!rect) return;
    onChange({ ...value, min_occupancy: Math.min(1, Math.max(0, v)) });
  };

  const rect = drag ? orderRect(drag.start, drag.cur) : rectFromValue(value);
  const pct = (n) => `${Math.round(n * 100)}%`;

  return (
    <div>
      <label style={labelStyle}>Stop box (detection region)</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <label style={smallBtn}>
          Upload reference image
          <input type="file" accept="image/*" onChange={onUpload} style={{ display: "none" }} />
        </label>
        {rect && (
          <button type="button" style={smallBtn} onClick={() => onChange(null)}>
            Clear box
          </button>
        )}
        <span style={{ fontSize: 11, color: C.textLight }}>
          {snapState === "loading"
            ? "Loading latest camera snapshot…"
            : "Click-drag on the image to draw where cars stop."}
        </span>
      </div>

      <div
        ref={wrapRef}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={finishDrag}
        onMouseLeave={finishDrag}
        style={{
          position: "relative",
          width: "100%",
          border: `1.5px solid ${C.border}`,
          borderRadius: 10,
          overflow: "hidden",
          cursor: "crosshair",
          userSelect: "none",
          background: "#e2e8f0",
        }}
      >
        {bgUrl ? (
          <img src={bgUrl} alt="camera view" draggable={false} style={{ display: "block", width: "100%", pointerEvents: "none" }} />
        ) : (
          <div style={{ width: "100%", paddingBottom: "56.25%", display: "flex" }}>
            <span style={{ margin: "auto", position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.textLight, fontSize: 12, textAlign: "center", padding: 16 }}>
              {snapState === "none"
                ? "No camera snapshot yet — upload a reference photo of the camera view to draw the stop box."
                : "…"}
            </span>
          </div>
        )}

        <svg viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
          {rect && (
            <rect
              x={rect.x1}
              y={rect.y1}
              width={rect.x2 - rect.x1}
              height={rect.y2 - rect.y1}
              fill="rgba(240,90,90,0.18)"
              stroke={C.coral}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      </div>

      {rect ? (
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: C.textMed }}>
            Box: {pct(rect.x1)},{pct(rect.y1)} → {pct(rect.x2)},{pct(rect.y2)}
            {value?.type === "polygon" ? " (polygon shown as bounding box; save replaces with rectangle)" : ""}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMed }}>
            Min. car overlap
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={value?.min_occupancy ?? DEFAULT_MIN_OCCUPANCY}
              onChange={(e) => setMinOccupancy(Number(e.target.value))}
              style={{ width: 64, borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, padding: "3px 6px" }}
            />
          </span>
        </div>
      ) : (
        <small style={{ color: C.textLight, fontSize: 11 }}>
          Optional. If unset, the detector treats the whole frame as the stop region.
        </small>
      )}
    </div>
  );
};

export default StopBoxEditor;
